import { useEffect, useState } from 'react'
import clsx from 'clsx'
import styles from './controls.module.css'
import FacetHistogram from './FacetHistogram'
import type { FilterRangeGroup } from 'lib/dealhunt/types'

interface RangeControlProps {
  group: FilterRangeGroup
  onCommit: (min: number | null, max: number | null) => void
}

export function formatUnit(
  value: number,
  unit: { prefix?: string; suffix?: string; decimals: number }
): string {
  const n = value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: unit.decimals,
  })
  return `${unit.prefix ?? ''}${n}${unit.suffix ?? ''}`
}

/** True when the other applied filters leave nothing with this spec: the group is
 *  still listed (the universe never shrinks) but has nothing to select. */
export function isRangeEmpty(group: FilterRangeGroup): boolean {
  return group.applied === null && group.stats.count === 0
}

/** True when the category holds ONE value for this spec ("Languages 100 – 100"): a
 *  slider with nothing between its ends. `scale` is the BASE population, so this is
 *  stable as other filters change — unlike `stats`, which is disjunctive (an emptied
 *  group stays listed and inert, see `isRangeEmpty`). An applied group is never
 *  degenerate: the reader must still be able to clear it. */
export function isRangeDegenerate(group: FilterRangeGroup): boolean {
  return group.applied === null && group.scale.min === group.scale.max
}

/**
 * The model's buckets span the FULL [min, max]; the slider spans [min, softCap].
 * Fold every bucket above the cap into one last "cap+" bucket so the chart and the
 * thumbs share a scale (otherwise a $240 selection lit a bucket mid-chart while the
 * thumb sat at the far right). `bucketWidth` is one raw bucket's span in scale units
 * — what maps a thumb value back to a bucket index.
 */
export function foldAboveCap(
  raw: number[],
  scale: { min: number; max: number; softCap?: number | null }
): { distribution: number[]; bucketWidth: number } {
  const fullSpan = scale.max - scale.min || 1
  const bucketWidth = fullSpan / (raw.length || 1)
  const cap = scale.softCap ?? scale.max
  if (cap >= scale.max || raw.length === 0) {
    return { distribution: raw, bucketWidth }
  }
  const keep = Math.max(1, Math.ceil((cap - scale.min) / bucketWidth))
  return {
    distribution: [...raw.slice(0, keep), raw.slice(keep).reduce((a, b) => a + b, 0)],
    bucketWidth,
  }
}

/**
 * Dual ("between") or single ("at least") slider over the model's scale, with the
 * distribution above it and a readout below. Drag state is local; the value
 * commits on release (one API call per gesture). `scale.softCap` truncates the
 * slider for long tails: the upper handle at the cap means "no upper bound".
 */
const RangeControl: React.VFC<RangeControlProps> = ({ group, onCommit }) => {
  const { scale, unit, mode, applied, stats } = group
  const empty = isRangeEmpty(group)
  const cap = scale.softCap ?? scale.max
  const capped = cap < scale.max
  const initialLo = applied?.min ?? scale.min
  const initialHi =
    applied?.max !== null && applied?.max !== undefined ? Math.min(applied.max, cap) : cap
  const [lo, setLo] = useState(initialLo)
  const [hi, setHi] = useState(initialHi)
  useEffect(() => {
    setLo(applied?.min ?? scale.min)
    setHi(applied?.max !== null && applied?.max !== undefined ? Math.min(applied.max, cap) : cap)
  }, [applied?.min, applied?.max, scale.min, cap])

  const span = cap - scale.min || 1
  const pctOf = (v: number): number => Math.max(0, Math.min(100, ((v - scale.min) / span) * 100))
  const commit = (nextLo: number, nextHi: number): void => {
    onCommit(
      nextLo <= scale.min ? null : nextLo,
      mode === 'atleast' || nextHi >= cap ? null : nextHi
    )
  }

  const { distribution, bucketWidth } = foldAboveCap(stats.distribution, scale)
  const buckets = distribution.length
  const bucketOf = (v: number): number => {
    if (capped && v >= cap) {
      return buckets - 1
    }
    return Math.max(0, Math.min(buckets - 1, Math.floor((v - scale.min) / bucketWidth)))
  }
  const isActive = applied !== null
  const activeFromIdx = bucketOf(lo)
  const activeToIdx = mode === 'atleast' || hi >= cap ? buckets - 1 : bucketOf(hi)
  let readout: string
  if (mode === 'atleast') {
    readout = lo <= scale.min ? 'Any' : `${formatUnit(lo, unit)} or more`
  } else if (hi >= cap && capped) {
    // Upper thumb at the soft cap = no upper bound.
    readout =
      lo <= scale.min
        ? `${formatUnit(scale.min, unit)} – ${formatUnit(cap, unit)}+`
        : `${formatUnit(lo, unit)}+`
  } else if (lo <= scale.min) {
    readout = `up to ${formatUnit(hi, unit)}`
  } else {
    readout = `${formatUnit(lo, unit)} – ${formatUnit(hi, unit)}`
  }

  return (
    <>
      {buckets > 0 && (
        <FacetHistogram
          distribution={distribution}
          activeFrom={activeFromIdx}
          activeTo={activeToIdx}
          compact
        />
      )}
      <div className={styles.slider}>
        <div className={styles.sliderTrack} />
        <div
          className={styles.sliderFill}
          style={{
            left: `${pctOf(lo)}%`,
            right: `${mode === 'atleast' ? 0 : 100 - pctOf(hi)}%`,
          }}
        />
        <input
          type="range"
          className={styles.sliderInput}
          min={scale.min}
          max={cap}
          step={scale.step}
          value={lo}
          disabled={empty}
          aria-label={`${group.label} minimum`}
          onChange={e =>
            setLo(
              mode === 'atleast'
                ? Number(e.target.value)
                : Math.min(Number(e.target.value), hi - scale.step)
            )
          }
          onPointerUp={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
        />
        {mode === 'between' && (
          <input
            type="range"
            className={styles.sliderInput}
            min={scale.min}
            max={cap}
            step={scale.step}
            value={hi}
            disabled={empty}
            aria-label={`${group.label} maximum`}
            onChange={e => setHi(Math.max(Number(e.target.value), lo + scale.step))}
            onPointerUp={() => commit(lo, hi)}
            onKeyUp={() => commit(lo, hi)}
          />
        )}
      </div>
      <div className={styles.sliderLabels}>
        <span>{formatUnit(scale.min, unit)}</span>
        <span>{capped ? `${formatUnit(cap, unit)}+` : formatUnit(scale.max, unit)}</span>
      </div>
      <div className={styles.readoutRow}>
        {empty ? (
          <span className={styles.emptyNote}>{'No matches have this spec'}</span>
        ) : (
          <span className={clsx(styles.readout, { [styles.readoutActive]: isActive })}>
            {readout}
          </span>
        )}
        {group.copy.foot && <span className={styles.foot}>{group.copy.foot}</span>}
      </div>
    </>
  )
}

export default RangeControl
